import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { Loader2, Search, User } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { APP_NAME } from "@/lib/brand";
import { AuthBackdrop } from "@/components/layout/AuthBackdrop";
import { enterGuest, guestName, leaveGuest } from "@/lib/guest";
import SearchPage from "@/pages/SearchPage";
import AlbumsPage from "@/pages/AlbumsPage";
import AlbumPage from "@/pages/AlbumPage";

export const GuestExpired = () => (
  <AuthBackdrop testid="guest-expired-page">
    <div className="auth-brand serif" data-testid="guest-expired-brand">{APP_NAME}</div>
    <h1 className="serif text-2xl text-neutral-900 mt-6">This link has expired.</h1>
    <p className="text-neutral-600 mt-3 text-sm">Ask the family for a new one.</p>
  </AuthBackdrop>
);

export const GuestLanding = () => {
  const { token } = useParams();
  const [bad, setBad] = useState(false);
  useEffect(() => {
    enterGuest(token).then(() => window.location.replace("/")).catch(() => setBad(true));
  }, [token]);
  if (bad) return <GuestExpired />;
  return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
};

const GuestBar = () => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const nav = ({ isActive }) => `topbar-link ${isActive ? "on" : ""}`;
  const leave = async () => { await leaveGuest(); window.location.replace("/login"); };
  return (
    <header className="topbar" data-testid="guest-topbar">
      <Link to="/" className="serif topbar-logo" data-testid="guest-logo"><span className="hidden sm:inline">{APP_NAME}</span><span className="sm:hidden">{APP_NAME.split(" ")[0]}</span></Link>
      <nav className="flex items-center gap-1">
        <NavLink to="/" end className={nav} data-testid="guest-nav-albums">Albums</NavLink>
      </nav>
      <form className="topbar-search" onSubmit={(e) => { e.preventDefault(); if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`); }} data-testid="guest-search-form">
        <Search size={15} className="topbar-search-icon" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guest photos…" data-testid="guest-search-input" />
      </form>
      <div className="ml-auto">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="icon-btn" aria-label="Account" data-testid="guest-menu-button"><User size={18} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="truncate" data-testid="guest-name">{guestName()}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={leave} data-testid="guest-leave-button">Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export const GuestApp = () => {
  const [state, setState] = useState("checking");
  const [daysLeft, setDaysLeft] = useState(null);
  useEffect(() => {
    api.get("/me").then((r) => { setDaysLeft(r.data.days_left); setState("ok"); })
      .catch((e) => setState(String(e.response?.data?.detail || "").includes("reviewed") ? "review" : "expired"));
    const id = api.interceptors.response.use((r) => r, (e) => {
      if (e.response?.status === 403 && String(e.response.data?.detail || "").includes("expired")) setState("expired");
      return Promise.reject(e);
    });
    return () => api.interceptors.response.eject(id);
  }, []);
  if (state === "checking") return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (state === "review") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="guest-review">
        <div className="bg-white rounded-2xl p-8 max-w-sm text-center shadow-xl">
          <h1 className="serif text-3xl text-neutral-900">Your access is being reviewed</h1>
          <p className="text-sm text-neutral-600 mt-3">The family checks new guest sign-ups. Try again a little later.</p>
          <button className="btn-ink w-full mt-6" onClick={() => window.location.reload()} data-testid="guest-review-retry">Check again</button>
        </div>
      </div>
    );
  }
  if (state === "expired") return <GuestExpired />;
  return (
    <div className="min-h-screen" data-testid="guest-shell">
      <GuestBar />
      {daysLeft != null && <div className="guest-banner" data-testid="guest-days-banner">Guest access · {daysLeft} day{daysLeft === 1 ? "" : "s"} left</div>}
      <main className="app-main">
        <Routes>
          <Route path="/" element={<AlbumsPage />} />
          <Route path="/albums" element={<AlbumsPage />} />
          <Route path="/albums/:id" element={<AlbumPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="*" element={<AlbumsPage />} />
        </Routes>
      </main>
    </div>
  );
};
